import { useEffect, useState } from 'react';
import { Briefcase, Building, Package, User } from 'lucide-react';
import { GroupContractDto } from 'api/contract';
import { contractsAPI } from 'services/swagger-api';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { InfoItem } from 'components/display/InfoItem';
import { GenericLoading } from 'components/ui/Loader';

interface ContractDetailLockedSummaryProps {
  groupContractId: string;
  /** Résolu côté card (le provider RoleContext n'est pas dans le portail du panel). */
  isAdmin: boolean;
}

/**
 * Résumé read-only des produits + courtiers du contrat. Réutilise le design de `PersonOverview` :
 * en-tête avatar rond + `CardsSection` + `InfoItem`.
 */
export const ContractDetailLockedSummary = ({ groupContractId, isAdmin }: ContractDetailLockedSummaryProps) => {

  const [contract, setContract] = useState<GroupContractDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await contractsAPI.getGroupContractByID({ groupContractId });
        if (!cancelled) setContract(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupContractId]);

  if (loading || !contract) return <GenericLoading loading isParentSized />;

  const activeTunnels = (contract.tunnels ?? []).filter(t => t.isActive);
  const activeBrokers = (contract.brokers ?? []).filter(b => b.isActive);

  return (
    <div className="space-y-6">
      {/* En-tête style PersonOverview */}
      <div className="flex items-center gap-4 pb-6 border-b border-primary/20">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Package className="w-8 h-8 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold text-gray-900 truncate">
            {contract.contractIdentifier || 'Contrat'}
          </h2>
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            <span className="text-primary font-medium">
              {activeTunnels.length} produit{activeTunnels.length > 1 ? 's' : ''}
            </span>
            {isAdmin && (
              <span className="text-gray-600 flex items-center gap-1">
                <User className="w-4 h-4" />
                {activeBrokers.length} courtier{activeBrokers.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      <CardsSection title={`Produits (${activeTunnels.length})`}>
        {activeTunnels.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Aucun produit actif.</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-6">
            {activeTunnels.map(tun => (
              <InfoItem
                key={tun.id}
                icon={<Briefcase />}
                label={tun.productName ?? tun.tunnelName ?? '—'}
                value={tun.tunnelCode}
              />
            ))}
          </div>
        )}
      </CardsSection>

      {isAdmin && (
        <CardsSection title={`Courtiers (${activeBrokers.length})`}>
          {activeBrokers.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Aucun courtier habilité.</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-6">
              {activeBrokers.map(b => (
                <InfoItem
                  key={b.id}
                  icon={<Building />}
                  label={b.brokerName ?? '—'}
                  value={b.brokerEmail}
                />
              ))}
            </div>
          )}
        </CardsSection>
      )}
    </div>
  );
};
